/**
 * The standing-idle library in play (lane C0-POSE-FIX, motions/idles.ts): which idle a standing figure shows. People
 * pick one of their group's idles (man, woman, elder, child) at random, seeded by their id and changed now and then
 * on their own clock, so neighbours never move in step; the player keeps one idle (by sex, until a setting picks it).
 * A figure with a weapon in hand only takes idles that leave the weapon hand free.
 *
 * Per idle: only the idles the user has approved in the studio's review (motions/review.ts, `idle:<id>`) are used; a
 * group with none approved keeps the old standing frames. The renderer asks this before its own standing frame.
 */
import type { Entity, World } from '@nine/shared';
import { idlesOf, type IdleDef, type IdleGroup } from './motions/idles';
import { approved } from './motions/review';
import { motionPhase, type MotionId } from './motions';
import { FRAMES, LOOKS, type SpriteSet } from './sprites';

/** The player's idle by sex until a setting picks one. */
const PLAYER_IDLE = { man: 'mRooted', woman: 'fPoised' } as const;
/** Seconds a person keeps one idle, at least and at most. */
const KEEP_MIN = 9;
const KEEP_MAX = 22;

/** A small integer hash (deterministic, no `Math.random`). */
function hash(n: number): number {
  let x = n | 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b);
  return (x ^ (x >>> 16)) >>> 0;
}

function groupOf(e: Entity): IdleGroup {
  const look = LOOKS[e.sprite];
  if (look?.age === 'elder') return 'elder';
  if (look?.age === 'child' || look?.age === 'teen') return 'child';
  return look?.female ? 'woman' : 'man';
}

/** The approved idles of a group (only those leaving the weapon hand free when armed), made once. */
const LISTS = new Map<string, IdleDef[]>();
function listOf(g: IdleGroup, armed: boolean): IdleDef[] {
  const k = `${g}:${armed}`;
  let l = LISTS.get(k);
  if (!l) LISTS.set(k, (l = idlesOf(g).filter((d) => (!armed || d.armed) && approved(`idle:${d.id}`))));
  return l;
}

/** The idle `e` stands in at time `t`, and how far into it it is (0–1). */
export function idleAt(
  e: Entity,
  player: boolean,
  armed: boolean,
  t: number,
): { def: IdleDef; u: number } | null {
  const g = groupOf(e);
  const list = listOf(g, armed);
  if (!list.length) return null;
  if (player) {
    const id = g === 'woman' ? PLAYER_IDLE.woman : PLAYER_IDLE.man;
    const def = list.find((d) => d.id === id);
    if (!def) return null;
    return { def, u: (t / def.secs) % 1 };
  }
  // Each person's own clock: an offset and how long they keep an idle.
  const h = hash(e.id * 2654435761);
  const keep = KEEP_MIN + ((h % 1000) / 1000) * (KEEP_MAX - KEEP_MIN);
  const tt = t + (h % 997) / 10;
  const span = Math.floor(tt / keep);
  const def = list[hash(e.id * 31 + span) % list.length]!;
  return { def, u: ((tt % keep) / def.secs) % 1 };
}

/** The frame of the idle library a standing figure shows, or null (off, or not a rig figure). */
export function idleLibFrame(e: Entity, set: SpriteSet, world: World): HTMLCanvasElement | null {
  if (!set.extra || e.dead) return null;
  const at = idleAt(e, e === world.player, !!set.armed, world.time);
  if (!at) return null;
  // 26 frames a loop: the first half's 14, then the second's inner 12 (motions/idles.ts).
  const f = Math.floor(at.u * 26) % 26;
  const id = at.def.id;
  const ph =
    f < FRAMES
      ? motionPhase(`idl:${id}:a` as MotionId, f)
      : motionPhase(`idl:${id}:b` as MotionId, f - FRAMES + 1);
  // A step not drawn yet holds the figure's last idle frame in this view, not the plain standing frame: offering
  // `idle[0]` (arms down, eyes open) popped it in between every new step of a first loop — every figure flickered.
  let held = HELD.get(set);
  if (!held) HELD.set(set, (held = new Map()));
  const prev = held.get(e.id);
  const img = set.extra('motion', ph, false, () => prev ?? set.idle[0]!);
  held.set(e.id, img);
  return img;
}

/** By sprite set (one view of one look), each figure's last idle-library frame shown. */
const HELD = new WeakMap<SpriteSet, Map<number, HTMLCanvasElement>>();
